import { useContext } from "react";
import { captureMessage } from "@sentry/browser";
import { useModal } from "react-modal-hook";
import { canDeleteComment } from "common/authorization/customPolicies";
import { COMMENT_OBJECT_TYPE } from "common/constants";
import { ActivityTargetType, ActivityType } from "../../generated/graphql";
import { formatDateString } from "../../utils/dates";
import { AuthContext } from "../Authorization/AuthContext";
import { ActionButton } from "../Common/Buttons/ActionButton";
import Modal from "../Common/Modal";
import { Body } from "../Common/Typography";
import {
  ActivityRow,
  Container,
  Content,
  TextContainer,
} from "./__styles__/ListItem";
import Bullet from "./Bullet";
import Comment from "./Comment";
import { DeleteActivityConfirmation } from "./DeleteActivityConfirmation";
import Message from "./Message";
import { FeedSource } from "./MessagePartBuilders/types";
import {
  Activity,
  isAdminActivity,
  isApiActivity,
  isCommentActivity,
  isGuestActivity,
  isPublicUserActivity,
  isUserActivity,
} from "./types";

interface ListItemProps {
  activity: Activity;
  onUpdate: (deletedId?: string) => void;
  linkToObject?: boolean;
  truncateComments: boolean;
  allowEdits: boolean;
  feedSource: FeedSource;
}

const ListItem = ({
  activity,
  onUpdate,
  linkToObject,
  feedSource,
  truncateComments,
  allowEdits,
}: ListItemProps) => {
  const { user, admin, account } = useContext(AuthContext);
  const [showDeleteActivityModal, hideDeleteActivityModal] = useModal(() => (
    <Modal onRequestClose={hideDeleteActivityModal}>
      <DeleteActivityConfirmation
        activity={activity}
        closeModal={hideDeleteActivityModal}
        onUpdate={onUpdate}
      />
    </Modal>
  ));

  const currentUser = user || admin;
  let creator: string;
  if (
    isAdminActivity(activity) ||
    (activity.targetType === ActivityTargetType.CERTIFICATE &&
      activity.type === ActivityType.CREATED)
  ) {
    creator = "Forerunner";
  } else if (isGuestActivity(activity)) {
    creator = "Guest";
  } else if (isApiActivity(activity)) {
    creator = activity.createdBy?.name ?? "API";
  } else if (isUserActivity(activity) && activity.createdBy) {
    const isCreator = activity.createdBy.id === currentUser?.id;
    creator =
      isCreator ? "You" : (
        `${activity.createdBy.firstName} ${activity.createdBy.lastName}`
      );
  } else if (isPublicUserActivity(activity)) {
    creator = `${activity.createdBy.firstName} ${activity.createdBy.lastName}`;
  } else {
    captureMessage(`Unknown activity creator type for activity ${activity.id}`);
    creator = "Forerunner";
  }

  if (activity.isGroup && !activity.targetInfo.totalCount) {
    return null;
  }

  const isDeletableAsAdmin =
    currentUser?.role.name === "admin" &&
    !(isCommentActivity(activity) && feedSource === "Property");

  const isDeletableComment =
    isCommentActivity(activity) &&
    canDeleteComment({
      user: {
        id: currentUser!.id,
        role: currentUser!.role.name,
        accountId: account!.id,
      },
      comment: {
        createdBy: activity!.createdBy!,
        accountId: account!.id,
      },
    }) &&
    allowEdits &&
    activity.targetInfo.objectType !== COMMENT_OBJECT_TYPE.SUBMISSION;

  const isDeletable = isDeletableAsAdmin || isDeletableComment;

  return (
    <Container>
      <ActivityRow isDeletable={isDeletable}>
        <Bullet activity={activity} />
        <Content>
          <TextContainer>
            <Message
              creator={creator}
              activity={activity}
              linkToObject={linkToObject}
              feedSource={feedSource}
            />
          </TextContainer>
          {isCommentActivity(activity) && activity.target && (
            <Comment
              content={activity.target.content}
              truncate={truncateComments}
            />
          )}
          <Body size="default" type="regular" color="contentSecondary">
            {formatDateString({
              dateString: activity.occurredAt,
              format: "HH:mm ZZ",
            })}
          </Body>
        </Content>
        {isDeletable && (
          <ActionButton
            icon="x"
            onClick={showDeleteActivityModal}
            data-testid="deleteActivity"
            aria-label="Delete activity"
          />
        )}
      </ActivityRow>
    </Container>
  );
};

export default ListItem;
